import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const lastEntry = (date) => (date
  ? `Last entry ${new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
  : 'No entries yet');

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const byName = (a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
const SORTS = {
  latest: { label: 'Latest entry', compare: (a, b) => (b.lastEntryDate || '').localeCompare(a.lastEntryDate || '') || byName(a, b) },
  count: { label: 'Number of entries', compare: (a, b) => b.entryCount - a.entryCount || byName(a, b) },
  name: { label: 'Name', compare: byName },
};

const shortDate = (date) => new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const hasGoals = (g) => (g.activeGoals || []).length > 0;

// Staff list of gymnasts who have written in their training diary or set a
// goal, with the goals they're working on. Empty diaries are left out.
export default function DiaryOverview() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('latest');

  useEffect(() => {
    axios.get('/api/training-diary/overview')
      .then(res => setRows(res.data))
      .catch(() => setError('Could not load the training diaries.'));
  }, []);

  return (
    <div className="container">
      <h2>Training diaries</h2>
      {error && <p className="bk-error">{error}</p>}
      {rows && (() => {
        const written = rows.filter(g => g.entryCount > 0 || hasGoals(g)).sort(SORTS[sort].compare);
        const empty = rows.length - written.length;
        if (written.length === 0) {
          return <p>{rows.length === 0 ? "No gymnasts have a diary yet. Turn it on from a gymnast's Diary tab." : 'Nobody has written in their diary or set a goal yet.'}</p>;
        }
        return (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>
                {plural(written.length, 'gymnast')}
                {empty > 0 && ` · ${empty} more with a diary but nothing in it yet`}
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, fontWeight: 400 }}>
                Sort by
                <select className="form-control" style={{ width: 'auto' }} value={sort} onChange={e => setSort(e.target.value)}>
                  {Object.entries(SORTS).map(([key, s]) => <option key={key} value={key}>{s.label}</option>)}
                </select>
              </label>
            </div>
            {/* A list rather than a .table: App.css hides tables on phones. */}
            <ul className="card" style={{ listStyle: 'none', padding: '0 1rem', margin: 0 }}>
              {written.map((g, i) => (
                <li key={g.id} style={{ padding: '0.75rem 0', borderTop: i ? '1px solid rgba(0,0,0,0.1)' : 'none' }}>
                  <Link to={`/progress/${g.id}?tab=diary`} style={{ fontWeight: 600, color: 'var(--secondary-color)', textDecoration: 'none' }}>{g.firstName} {g.lastName}</Link>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.15rem' }}>
                    {lastEntry(g.lastEntryDate)} · {plural(g.entryCount, 'entry', 'entries')} · {g.entriesLast28Days} in the last 28 days
                  </div>
                  {hasGoals(g) && (
                    <div style={{ fontSize: '0.9rem', marginTop: '0.35rem' }}>
                      <strong>{g.activeGoals.length === 1 ? 'Goal' : 'Goals'}:</strong>{' '}
                      {g.activeGoals.map((goal, j) => (
                        <span key={goal.id}>
                          {j > 0 && ' · '}
                          {goal.title}
                          {goal.targetDate && <span style={{ color: '#666' }}> (by {shortDate(goal.targetDate)})</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        );
      })()}
    </div>
  );
}
